import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation, Mousewheel, A11y, Keyboard } from 'swiper'
import { ApiQuery, findApiV4Link, useJsonApi } from 'shared'
import { PictureArray, PictureProps, PicturesApiResult } from './types'
import { NavigationArrow } from 'shared'
import { VideoPlayer } from 'modules'
import classNames from 'classnames'

SwiperCore.use([Navigation, Mousewheel, A11y, Keyboard])

export const Gallery = ({
  profilePictureUrl,
  videoEmbedUrl,
  videoThumbnailUrl,
  apiUrl,
}: {
  profilePictureUrl?: string
  videoEmbedUrl?: string
  videoThumbnailUrl?: string
  apiUrl: string
}): JSX.Element | null => {
  const [page, setPage] = useState(1)
  const [pictures, setPictures] = useState<PictureArray>([])
  const query = new ApiQuery(apiUrl)
  const currentPageUrl = query.per(3).page(page).toUrl()
  const { data, loading } = useJsonApi<PicturesApiResult>(currentPageUrl)
  const [hasMultipleItems, setHasMultipleItems] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [showVideoSlide, setShowVideoSlide] = useState(true)

  // Whenever new pictures have been loaded, add them to the stack
  useEffect(() => {
    loading || (data?.data && setPictures((pics) => pics.concat(data.data)))
  }, [loading, data?.data])

  useEffect(() => {
    if (data?.total_entries || videoEmbedUrl) {
      setHasMultipleItems(true)
    }
  }, [data?.total_entries, videoEmbedUrl])

  // Swiper ignores updates to onReachEnd, so use a ref.
  const pageRef = useRef(1)
  const onReachEnd = useCallback(() => setPage((pageRef.current += 1)), [])
  const additionalImages = pictures?.map((picture, index) => (
    <SwiperSlide key={`pic-${picture.id}`}>
      <Picture {...picture} index={index} total={data?.total_entries || undefined} close={close} />
    </SwiperSlide>
  ))

  if (!hasMultipleItems)
    return (
      <div className="projects-show-gallery" data-testid="gallery">
        <div className="image-container covering-images">
          <img src={profilePictureUrl} className="single-image covering-image" />
        </div>
      </div>
    )
  else
    return (
      <div className="projects-show-gallery" data-testid="gallery">
        <Swiper
          slidesPerView={1}
          navigation={{
            nextEl: '#gallery-button-next',
            prevEl: '#gallery-button-prev',
            disabledClass: 'is-disabled',
          }}
          keyboard
          centeredSlides
          roundLengths
          mousewheel={{ forceToAxis: true }}
          onReachEnd={onReachEnd}
          onSlideChange={(e) => setActiveIndex(e.activeIndex)}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          a11y={{
            enabled: true,
          }}
        >
          {videoEmbedUrl && showVideoSlide && (
            <SwiperSlide>
              <div className="gallery-video">
                <VideoPlayer
                  videoUrl={videoEmbedUrl}
                  thumbnailUrl={videoThumbnailUrl}
                  activeIndex={activeIndex}
                  setShowVideoSlide={setShowVideoSlide}
                />
              </div>
            </SwiperSlide>
          )}
          {profilePictureUrl && (
            <SwiperSlide>
              <img src={profilePictureUrl} className="gallery-image covering-image" />
            </SwiperSlide>
          )}
          {additionalImages}
          <div className={classNames('gallery-control')}>
            <button className="gallery-button gallery-button-prev" id="gallery-button-prev">
              <NavigationArrow direction="left" additionalClassName="ml-0" size="small" />
            </button>
            <button className="gallery-button gallery-button-next" id="gallery-button-next">
              <NavigationArrow direction="right" additionalClassName="ml-0" size="small" />
            </button>
          </div>
        </Swiper>
      </div>
    )
}

const Picture = ({ close, index, total, ...picture }: PictureProps): JSX.Element => {
  const srcUrl = findApiV4Link({ ofObject: picture, withRel: 'image' })
  const [showPictureInfo, setShowPictureInfo] = useState(false)

  return (
    <figure>
      <img src={srcUrl} className="gallery-image" />
      {picture.description && (
        <>
          <button className="btn-over-image picture-info-trigger" onClick={() => setShowPictureInfo(true)}>
            <i className="fa fa-info-circle pr-1" style={{ color: 'white' }}></i>
            <span>{I18n.t('ppp.gallery.info_trigger')}</span>
          </button>
          <figcaption className={`picture-info ${showPictureInfo ? 'flex' : 'hidden'}`}>
            <span>{picture.description}</span>
            <button onClick={() => setShowPictureInfo(false)}>
              <i className="fas fa-times" style={{ color: 'white' }}></i>
            </button>
          </figcaption>
        </>
      )}
    </figure>
  )
}
